import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { get, isEmpty, isEqual } from 'lodash';
import { CSpinner } from '@coreui/react';
import GeneralLayoutHeader from './GeneralLayoutHeader';
import GeneralLayoutSideMenu, { SideBarState } from './GeneralLayoutSideMenu';
import GeneralLayoutContent from './GeneralLayoutContent';
import GeneralLayourFooter from './GeneralLayoutFooter';
import { selectUserProfile } from '../store/selectors/profile.selectors';
import {
  selectJourneyConfig,
  selectJourneyConfigLoaded,
  selectJourneyConfigLoading,
} from '../store/selectors/journey.selectors';
import { GetActiveConfig } from '../customization/actions/journey';
import { JOURNEY_AGENT_LABEL_PATH, JOURNEY_TASK_LABEL_PATH } from '../customization/constants';
import { ConfigContext } from '../shared/contexts/journeyConfig.context';
import { UserRoles } from '../shared/models/user';
import { getSelectedOrgId } from '../shared/constants/orgHierarchy.constants';
import { LoadOrgList, LoadSelectedOrg } from '../store/actions/orgHierarchy.action';
import { selectOrgHierarchyActiveNode, selectOrgHierarchySelectedOrg } from '../store/selectors/orgHierarchy.selectors';
import { selectUserOrganization } from '../store/selectors/store.selectors';
import { CUSTOMIZATION_ENABLED_TAG } from '../shared/constants/tags';

const NON_PRO_LABELS = {
  agent: 'Technician',
  task: 'Task',
};

const GeneralLayout = () => {
  const [showMenu, setShowMenu] = useState<SideBarState>(false);
  const [hasNoAccess, setHasNoAccess] = useState(false);
  const [configLabels, setConfigLabels] = useState({
    agent: 'Technician',
    task: 'Job'
  });

  const dispatch = useDispatch();
  const profile = useSelector(selectUserProfile);
  const config = useSelector(selectJourneyConfig);
  const isConfigLoading = useSelector(selectJourneyConfigLoading);
  const isConfigLoaded = useSelector(selectJourneyConfigLoaded);
  const activeNode = useSelector(selectOrgHierarchyActiveNode);
  const org = useSelector(selectUserOrganization);
  const selectedOrg = useSelector(selectOrgHierarchySelectedOrg);

  useEffect(() => {
    if (selectedOrg && profile) {
      const isAdmin = profile.roles.indexOf(UserRoles.Admin) > -1;
      const isManager = profile.roles.indexOf(UserRoles.Manager) > -1;
      const isAdminOrManager = isAdmin || isManager;
      if (selectedOrg?._id && isAdminOrManager && (
        org?.referrer_org_id === parseInt(process.env.ORGANIZATION_SIGNUP_REFERRER_ID, 10) ||
        org?.referrer_org_id === parseInt(process.env.ORGANIZATION_SIGNUP_REFERRER_ID_S1, 10) ||
        org?.referrer_org_id === parseInt(process.env.ORGANIZATION_SIGNUP_REFERRER_ID_S0_REST, 10) ||
        org?.tags?.indexOf(CUSTOMIZATION_ENABLED_TAG) > -1)
      ) {
        setHasNoAccess(false);
        dispatch(new GetActiveConfig(org?._id));
      } else {
        setHasNoAccess(true);
      }
    }
  }, [selectedOrg, profile?.orgId, org?.referrer_org_id]);

  useEffect(() => {
    if (
      org && 
      org.referrer_org_id !== parseInt(process.env.ORGANIZATION_SIGNUP_REFERRER_ID, 10) && 
      org.referrer_org_id !== parseInt(process.env.ORGANIZATION_SIGNUP_REFERRER_ID_S1, 10) &&
      org.referrer_org_id !== parseInt(process.env.ORGANIZATION_SIGNUP_REFERRER_ID_S0_REST, 10)
    ) {
      setConfigLabels(NON_PRO_LABELS);
    }
  }, [org]);

  useEffect(() => {
    if (config) {
      const newConfigLabels = {
        agent: get(config, JOURNEY_AGENT_LABEL_PATH) || 'Technician',
        task: get(config, JOURNEY_TASK_LABEL_PATH) || 'Job'
      };
      if (!isEqual(newConfigLabels, configLabels)) {
        setConfigLabels(newConfigLabels)
      }
    }
  }, [config]);

  useEffect(() => {
    if (org && !activeNode && !selectedOrg) {
      let activeOrg;
      try {
        activeOrg = localStorage.getItem(getSelectedOrgId(profile.id, org._id));
      } catch (e) {
        // eslint-disable-next-line no-console
        console.log(e);
      }
      dispatch(new LoadSelectedOrg(+activeOrg || org?._id));
    }
  }, [org, activeNode, selectedOrg]);

  useEffect(() => {
    dispatch(new LoadOrgList());
  }, []);

  const configLoadingComplete = isConfigLoaded && !isConfigLoading && (!isEmpty(config)) || hasNoAccess;

  return (
    <ConfigContext.Provider value={configLabels} >
      { !configLoadingComplete &&
        <div className="c-app c-default-layout flex-row align-items-center justify-content-center">
          <CSpinner
            className="primary-color"
            style={{width:'3rem', height:'3rem'}}
          />
        </div>
      }
      {
        configLoadingComplete && (
          <div className="c-app c-default-layout">
            <GeneralLayoutSideMenu showMenu={showMenu} onMenuToggle={setShowMenu} />
            <div className="c-wrapper">
              <GeneralLayoutHeader
                showMenu={showMenu}
                onMenuToggle={setShowMenu}
              />
              <div className="c-body">
                <GeneralLayoutContent />
              </div>
              <GeneralLayourFooter/>
            </div>
          </div>
        )
      }
    </ConfigContext.Provider>
  );
};

export default GeneralLayout;
